/**
 * balanceOverrides.ts — BETA 21.62 Balance Dashboard runtime override layer.
 *
 * Dev-tuned balance numbers live in a single Supabase row (`balance_overrides`,
 * id='global', a JSON blob). On app startup we fetch them and apply them ON TOP of
 * the code-default game data (by mutating the live data objects via balanceData's
 * field map), so the dashboard can edit values that take effect with NO redeploy.
 *
 * Shape: { [category]: { [entityId]: { [fieldKey]: number } } }
 * e.g. { items: { C06: { "mod:armor": 3 } }, perks: { T11: { bonusPerLevel: 3 } } }
 *
 * Claude can read this same row via the Supabase REST API to see exactly what Dylan
 * changed and bake it into the .ts source when finalized.
 */

const SUPA_URL = 'https://wslppieqghdyaodhbrbl.supabase.co/rest/v1/balance_overrides';
const SUPA_KEY = 'sb_publishable_7uHojLtW1sWE9risVX2CcA_Tmqk6sgY';

// Values are numbers for most fields, but strings for the rarity dropdown.
export type OverrideValue = number | string;
export type OverrideMap = Record<string, Record<string, Record<string, OverrideValue>>>;

let _overrides: OverrideMap = {};
let _loaded = false;
let _saveTimer: ReturnType<typeof setTimeout> | null = null;

export function getOverrides(): OverrideMap {
  return _overrides;
}

/**
 * Read the dashboard "Spawn weight" off any game object (default 1 = unchanged). The
 * Balance Dashboard writes it to obj.__spawnWeight; offer rollers multiply selection
 * odds by it. Lives here (not balanceData) so itemsAdapter can import it without a cycle.
 */
export function spawnWeightOf(obj: unknown): number {
  const w = (obj as { __spawnWeight?: number })?.__spawnWeight;
  return typeof w === 'number' && w > 0 ? w : 1;
}

export function getOverride(cat: string, id: string, field: string): OverrideValue | undefined {
  return _overrides[cat]?.[id]?.[field];
}

// BETA 22.71 — Cosmetics Editor transforms REMOVED (cosmetics system deleted).
// Old rows may remain under the 'cosmetics' category in Supabase — unread now.

export function setOverrideLocal(cat: string, id: string, field: string, value: OverrideValue): void {
  (_overrides[cat] ||= {});
  (_overrides[cat][id] ||= {});
  _overrides[cat][id][field] = value;
}

export function clearOverrideLocal(cat: string, id: string, field: string): void {
  if (_overrides[cat] && _overrides[cat][id]) {
    delete _overrides[cat][id][field];
    if (Object.keys(_overrides[cat][id]).length === 0) delete _overrides[cat][id];
    if (Object.keys(_overrides[cat]).length === 0) delete _overrides[cat];
  }
}

export function isBalanceLoaded(): boolean {
  return _loaded;
}

/** Count of overridden fields (for the dashboard "N changes" badge). */
export function overrideCount(): number {
  let n = 0;
  for (const cat of Object.values(_overrides)) {
    for (const ent of Object.values(cat)) n += Object.keys(ent).length;
  }
  return n;
}

/** Fetch the dev overrides from Supabase. Call once at app startup. */
export async function loadBalanceOverrides(): Promise<OverrideMap> {
  try {
    const res = await fetch(`${SUPA_URL}?id=eq.global&select=data`, {
      headers: { apikey: SUPA_KEY },
    });
    if (res.ok) {
      const rows = await res.json();
      const data = rows?.[0]?.data;
      if (data && typeof data === 'object') _overrides = data as OverrideMap;
    }
  } catch {
    // offline / Supabase down → fall back to code defaults (no overrides)
  }
  _loaded = true;
  return _overrides;
}

/** Persist the full override blob to Supabase. Debounced so rapid edits batch. */
export function saveBalanceOverridesDebounced(updatedBy = 'dev'): void {
  if (_saveTimer) clearTimeout(_saveTimer);
  _saveTimer = setTimeout(() => { void saveBalanceOverrides(updatedBy); }, 600);
}

export async function saveBalanceOverrides(updatedBy = 'dev'): Promise<boolean> {
  try {
    const res = await fetch(`${SUPA_URL}?id=eq.global`, {
      method: 'PATCH',
      headers: {
        apikey: SUPA_KEY,
        'Content-Type': 'application/json',
        Prefer: 'return=minimal',
      },
      body: JSON.stringify({ data: _overrides, updated_by: updatedBy }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Export the current overrides as a pretty JSON string (for Claude to bake to code). */
export function exportOverridesJson(): string {
  return JSON.stringify(_overrides, null, 2);
}
